/**
 * Which keyboard, if any, goes with a language — and what kind of keyboard it is.
 *
 * ── What this generalises ───────────────────────────────────────────────────
 * The product shipped one keyboard, for Korean, because Korean was the
 * language the owner watched somebody fail to type. Nothing about that problem
 * is Korean. A visitor who names Greek, Thai, Hindi or Russian as their input
 * language is in exactly the same position: they have told us what they are
 * about to write, and their machine has no way to write it.
 *
 * So the switch is now per language, and the only thing that varies is what
 * pressing a key MEANS. There are four answers, and they are genuinely
 * different problems rather than four settings of one:
 *
 *   • `direct` — one key, one character. Russian, Greek, Hebrew, Arabic, Thai,
 *     the Indic scripts, and thirty more. The layouts are the real national
 *     ones, generated from `xkb-data` (see scripts/build-keyboards.mjs), plus
 *     dead keys for the scripts that need them.
 *   • `hangul` — Korean, where three keys make one syllable block and the
 *     block is rebuilt on every keystroke. lib/hangulIme.ts, unchanged.
 *   • `romaji` — Japanese, where Latin spelling becomes kana by rule. No
 *     dictionary, no candidates, and no kanji — see lib/romajiKana.ts for why
 *     that is the honest stopping point rather than a half-finished IME.
 *   • `pinyin` — Chinese, the only one that cannot be done by rule at all:
 *     `zhong` is a hundred characters and a person has to pick. It is the only
 *     engine with a downloaded dictionary and a candidate bar.
 *
 * ── Why the layout id is not the language code ──────────────────────────────
 * Because nine languages type on Devanagari InScript and three on the Hebrew
 * layout. One table per LAYOUT, and this file points languages at it. Holding
 * Devanagari nine times would be eight chances for one copy to drift.
 */

import { KEYBOARD_LAYOUTS, type GeneratedLayout } from './keyboardLayouts.generated';

/** An edit to apply at the caret: delete `back` characters, then type `insert`. */
export interface ImeEdit {
  back: number;
  insert: string;
}

export type KeyboardEngine = 'direct' | 'hangul' | 'romaji' | 'pinyin';

export interface KeyboardSpec {
  engine: KeyboardEngine;
  /** A key in KEYBOARD_LAYOUTS — `direct` only; the other engines draw their own. */
  layoutId?: string;
  /** The writing system, for "your keys type ⟨Cyrillic⟩". */
  script: string;
  /**
   * Said out loud on the panel when the board is not this language's OWN.
   *
   * Only ever an honest caveat, never an apology: Tuvan really is typed on a
   * Russian keyboard, and Japanese really does come out as kana. A reader who
   * is surprised by either deserves to have been told.
   */
  note?: string;
}

/**
 * Cyrillic languages with no keyboard of their own.
 *
 * All of them are written in Russia, all of them are typed on ЙЦУКЕН, and XKB
 * does ship a variant for several — but every one of those variants is the
 * Russian layout with a few letters added on AltGr, a level this panel has no
 * way to press. Shipping them as separate "Chuvash" or "Udmurt" keyboards
 * would have been a Russian keyboard with another language's name on it, which
 * is a worse lie than the note below.
 *
 * Bashkir and Tatar are NOT in here: both put their own letters on levels a
 * person can reach, so both get real layouts.
 */
const CYRILLIC_ON_RUSSIAN = [
  'ab', 'av', 'bua', 'ce', 'cv', 'crh-Cyrl', 'kv', 'chm', 'os', 'tyv', 'udm', 'sah',
];

/** Devanagari languages that all type on InScript. Nepali has its own. */
const DEVANAGARI = ['hi', 'mr', 'sa', 'awa', 'bho', 'doi', 'gom', 'mai', 'mwr', 'new'];

/** Languages written in the Arabic script that use the plain Arabic board. */
const ARABIC_PLAIN = ['bal', 'ks', 'ms-Arab'];

function entries(): Record<string, KeyboardSpec> {
  const map: Record<string, KeyboardSpec> = {
    // ── The three engines that are not a plain layout ───────────────────────
    ko: { engine: 'hangul', script: 'Hangul' },
    ja: {
      engine: 'romaji',
      script: 'kana',
      note: 'Type it as you would say it — kana, not kanji.',
    },
    zh: { engine: 'pinyin', script: 'Chinese' },
    'zh-TW': { engine: 'pinyin', script: 'Chinese' },
    yue: { engine: 'pinyin', script: 'Chinese', note: 'Mandarin pinyin — Cantonese jyutping is not supported.' },

    // ── Cyrillic ────────────────────────────────────────────────────────────
    ru: { engine: 'direct', layoutId: 'ru', script: 'Cyrillic' },
    uk: { engine: 'direct', layoutId: 'uk', script: 'Cyrillic' },
    be: { engine: 'direct', layoutId: 'be', script: 'Cyrillic' },
    bg: { engine: 'direct', layoutId: 'bg', script: 'Cyrillic' },
    sr: { engine: 'direct', layoutId: 'sr', script: 'Cyrillic' },
    mk: { engine: 'direct', layoutId: 'mk', script: 'Cyrillic' },
    kk: { engine: 'direct', layoutId: 'kk', script: 'Cyrillic' },
    ky: { engine: 'direct', layoutId: 'ky', script: 'Cyrillic' },
    tg: { engine: 'direct', layoutId: 'tg', script: 'Cyrillic' },
    mn: { engine: 'direct', layoutId: 'mn', script: 'Cyrillic' },
    tt: { engine: 'direct', layoutId: 'tt', script: 'Cyrillic' },
    ba: { engine: 'direct', layoutId: 'bak', script: 'Cyrillic' },

    // ── Greek and Hebrew ────────────────────────────────────────────────────
    el: { engine: 'direct', layoutId: 'el', script: 'Greek' },
    he: { engine: 'direct', layoutId: 'he', script: 'Hebrew' },
    yi: { engine: 'direct', layoutId: 'he', script: 'Hebrew', note: 'The Hebrew keyboard — Yiddish has no separate layout.' },

    // ── Arabic script ───────────────────────────────────────────────────────
    ar: { engine: 'direct', layoutId: 'ar', script: 'Arabic' },
    fa: { engine: 'direct', layoutId: 'fa', script: 'Persian' },
    prs: { engine: 'direct', layoutId: 'fa', script: 'Persian' },
    ur: { engine: 'direct', layoutId: 'ur', script: 'Urdu' },
    'pa-Arab': { engine: 'direct', layoutId: 'ur', script: 'Shahmukhi', note: 'The Urdu keyboard, which is what Shahmukhi is typed on.' },
    ps: { engine: 'direct', layoutId: 'ps', script: 'Pashto' },
    sd: { engine: 'direct', layoutId: 'sd', script: 'Sindhi' },
    ug: { engine: 'direct', layoutId: 'ug', script: 'Uyghur' },
    ckb: { engine: 'direct', layoutId: 'ckb', script: 'Kurdish' },

    // ── Southeast Asia ──────────────────────────────────────────────────────
    th: { engine: 'direct', layoutId: 'th', script: 'Thai' },
    lo: { engine: 'direct', layoutId: 'lo', script: 'Lao' },
    km: { engine: 'direct', layoutId: 'km', script: 'Khmer' },
    my: { engine: 'direct', layoutId: 'my', script: 'Burmese' },
    shn: { engine: 'direct', layoutId: 'shn', script: 'Shan' },

    // ── South Asia ──────────────────────────────────────────────────────────
    ne: { engine: 'direct', layoutId: 'ne', script: 'Devanagari' },
    bn: { engine: 'direct', layoutId: 'ben', script: 'Bengali' },
    as: { engine: 'direct', layoutId: 'asm', script: 'Assamese' },
    trp: { engine: 'direct', layoutId: 'ben', script: 'Bengali' },
    pa: { engine: 'direct', layoutId: 'guru', script: 'Gurmukhi' },
    gu: { engine: 'direct', layoutId: 'guj', script: 'Gujarati' },
    or: { engine: 'direct', layoutId: 'ori', script: 'Odia' },
    ta: { engine: 'direct', layoutId: 'tam', script: 'Tamil' },
    te: { engine: 'direct', layoutId: 'tel', script: 'Telugu' },
    kn: { engine: 'direct', layoutId: 'kan', script: 'Kannada' },
    tcy: { engine: 'direct', layoutId: 'kan', script: 'Kannada' },
    ml: { engine: 'direct', layoutId: 'mal', script: 'Malayalam' },
    si: { engine: 'direct', layoutId: 'si', script: 'Sinhala' },
    'mni-Mtei': { engine: 'direct', layoutId: 'mni', script: 'Meetei Mayek' },
    sat: { engine: 'direct', layoutId: 'sat', script: 'Ol Chiki' },

    // ── Caucasus, islands, plateaus ─────────────────────────────────────────
    hy: { engine: 'direct', layoutId: 'hy', script: 'Armenian' },
    ka: { engine: 'direct', layoutId: 'ka', script: 'Georgian' },
    dv: { engine: 'direct', layoutId: 'dv', script: 'Thaana' },
    bo: { engine: 'direct', layoutId: 'bo', script: 'Tibetan' },
    dz: { engine: 'direct', layoutId: 'bo', script: 'Tibetan' },

    // ── Africa and the Arctic ───────────────────────────────────────────────
    nqo: { engine: 'direct', layoutId: 'nqo', script: "N'Ko" },
    ber: { engine: 'direct', layoutId: 'ber', script: 'Tifinagh' },
    iu: { engine: 'direct', layoutId: 'iu', script: 'Syllabics' },
  };

  for (const code of CYRILLIC_ON_RUSSIAN) {
    map[code] = {
      engine: 'direct',
      layoutId: 'ru',
      script: 'Cyrillic',
      note: 'The Russian keyboard, which is what this language is typed on.',
    };
  }
  for (const code of DEVANAGARI) {
    map[code] = { engine: 'direct', layoutId: 'deva', script: 'Devanagari' };
  }
  for (const code of ARABIC_PLAIN) {
    map[code] = { engine: 'direct', layoutId: 'ar', script: 'Arabic' };
  }
  return map;
}

const KEYBOARDS = entries();

/**
 * The keyboard for a language, or null if there is nothing to offer.
 *
 * Null for every Latin-script language, which is the common case and the right
 * answer: a French speaker's keyboard already types French. Also null for
 * Amharic and Tigrinya — Ethiopic is a syllabary of several hundred glyphs and
 * thirty-three caps cannot hold it. An empty offer is better than a keyboard
 * that can type a tenth of a language.
 *
 * ⚠️ Also null for a `direct` language whose layout failed to generate, which
 * is the case the guard is really for: the registry is written by hand and the
 * layouts are generated, so the two can disagree after a regeneration, and a
 * missing layout must be a missing button rather than an empty panel.
 */
export function keyboardFor(code: string | null | undefined): KeyboardSpec | null {
  if (!code || code === 'auto') return null;
  const spec = KEYBOARDS[code];
  if (!spec) return null;
  if (spec.engine === 'direct' && !(spec.layoutId && KEYBOARD_LAYOUTS[spec.layoutId])) return null;
  return spec;
}

export function layoutFor(spec: KeyboardSpec): GeneratedLayout | null {
  return spec.layoutId ? KEYBOARD_LAYOUTS[spec.layoutId] ?? null : null;
}

/** Every language that has one — used by the tests and by nothing on screen. */
export function keyboardLanguages(): string[] {
  return Object.keys(KEYBOARDS).filter((c) => keyboardFor(c));
}
